import React, { Dispatch, FC, ReactNode, Reducer, useReducer } from 'react';

import { PlayerNotes_noteFragment } from '@/hooks/__generated__/usePlayerNotes';
import { ContextBuilder } from './ContextBuilder';

type NewNodeEntry<T> = T & { __isNew: true };

type State = {
  Note: NewNodeEntry<PlayerNotes_noteFragment>[];
};

type AddAction<NodeType extends keyof State> = {
  type: 'ADD_NODE';
  nodeType: NodeType;
  node: Omit<State[NodeType][number], '__isNew'>;
};

type Action = AddAction<keyof State>;

const NewNodesContext = new ContextBuilder<State>('NewNodesContext');
const DispatchNewNodesContext = new ContextBuilder<Dispatch<Action>>(
  'DispatchNewNodesContext',
);

export const useNewNodes = NewNodesContext.use;
export const useNewNodesOf = (t: keyof State) => useNewNodes()[t];
export const useDispatchNewNodes = DispatchNewNodesContext.use;

const reducer: Reducer<State, Action> = (state, action) => {
  switch (action.type) {
    case 'ADD_NODE':
      return {
        ...state,
        [action.nodeType]: [
          { ...action.node, __isNew: true },
          ...state[action.nodeType],
        ],
      };
    default:
      return state;
  }
};

export const NewNodesProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, {
    Note: [],
  });

  return (
    <DispatchNewNodesContext.Context.Provider value={dispatch}>
      <NewNodesContext.Context.Provider value={state}>
        {children}
      </NewNodesContext.Context.Provider>
    </DispatchNewNodesContext.Context.Provider>
  );
};
